import { useParams, Link } from "react-router-dom";
import { ChevronsRight } from "lucide-react";

const CONTENT = {
  "how-to-find-your-fuel-injector-part-number": {
    title: "How to Find Your Fuel Injector Part Number",
    body: [
      "Every fuel injector carries an identifier that ties it back to a specific vehicle application. The number is typically printed on the body of the injector, either laser-etched into the metal or on a small foil label near the connector.",
      "To locate it, first ensure the engine is cool and disconnect the negative battery terminal. Remove the engine cover, and locate the fuel rail. The injectors sit between the rail and the intake manifold.",
      "Write down the full number including any dashes and letters. Small differences (a trailing letter, or a suffix like '-A01') can indicate a different application year or emissions calibration.",
      "Enter the number into our Search by Part Number page — the search recognizes common formatting differences (spaces, dashes, capitalization). If you receive multiple partial matches, use the Fitment Assistance form to have our team verify the correct listing for your vehicle.",
    ],
  },
  "oem-vs-aftermarket-fuel-injectors": {
    title: "OEM vs. Aftermarket Fuel Injectors",
    body: [
      "OEM (Original Equipment Manufacturer) injectors are the exact parts supplied by the vehicle's manufacturer. They are the closest match to what the vehicle came with from the factory.",
      "Remanufactured injectors are used cores that have been disassembled, cleaned, tested, and rebuilt to meet specific flow and spray-pattern tolerances. On a well-inspected listing, they are a reliable and cost-effective option.",
      "Aftermarket injectors from independent manufacturers can be excellent, but quality varies widely. Look for reputable brands (Bosch, Denso, Delphi, Siemens) and clear application information.",
      "At InterCorp Industries, every listing clearly labels the condition (OEM, Remanufactured, or Used) so you can make an informed choice.",
    ],
  },
  "single-injector-vs-complete-injector-set": {
    title: "Single Injector vs. Complete Injector Set",
    body: [
      "If you have confirmed via diagnostic that only one injector is faulty, replacing that single injector is often adequate — especially on a lower-mileage engine.",
      "However, if the vehicle has high mileage or the failed injector shows contamination consistent with dirty fuel, replacing the full set can prevent successive failures.",
      "Sets are matched to the specific engine and are listed accordingly on our site. Always confirm the set quantity matches your engine's cylinder count.",
    ],
  },
  "what-remanufactured-fuel-injectors-mean": {
    title: "What Remanufactured Fuel Injectors Mean",
    body: [
      "Remanufactured injectors start life as used cores that are disassembled, ultrasonically cleaned, and reassembled with new internal seals, filters, and pintle caps.",
      "They are then bench-tested for correct flow rate, spray pattern, and leak-back. Listings that include test-report images document the actual measured values.",
      "Look for 'W/ Test Report' listings in our catalog when you want documented performance data before the injector ships.",
    ],
  },
  "direct-injection-vs-port-injection": {
    title: "Direct Injection vs. Port Injection",
    body: [
      "Port fuel injection (PFI) sprays fuel into the intake port ahead of the intake valve. Injectors are lower-pressure, easier to service, and used by most engines from the mid-1980s onward.",
      "Gasoline direct injection (GDI) sprays fuel directly into the combustion chamber under very high pressure. Injectors are more complex mechanically, more expensive, and are matched precisely to the engine's fuel-delivery calibration.",
      "Confirm which system your engine uses before ordering — the two are not interchangeable.",
    ],
  },
  "how-to-confirm-vehicle-compatibility": {
    title: "How to Confirm Vehicle Compatibility",
    body: [
      "Combine three pieces of information: (1) year/make/model/trim, (2) engine displacement and code, and (3) the original part number stamped on the injector you are replacing.",
      "The most reliable match uses the part number. Year/make/model narrows down candidates when the number is unreadable.",
      "Use our Fitment Assistance form to submit your VIN — we'll cross-reference against the listing data and reply with a recommendation.",
    ],
  },
  "common-symptoms-of-injector-problems": {
    title: "Common Symptoms of Injector Problems",
    body: [
      "Look for rough idle, misfires under load, fuel smell after shutdown, reduced fuel economy, and check-engine codes in the P020x range (cylinder-specific misfires).",
      "Injector issues can mimic ignition problems — verify with a fuel-pressure test and, ideally, a bench-test of the suspect injector before assuming failure.",
    ],
  },
  "why-engine-size-matters": {
    title: "Why Engine Size Matters",
    body: [
      "Two vehicles that share a model year and make often ship with multiple engine options. Each engine typically has its own injector part number matched to its fuel-flow requirements.",
      "Always confirm engine displacement (e.g. 2.4L vs 2.7L) and, when available, the engine family code (e.g. 2AZ-FE) before ordering.",
    ],
  },
};

export default function Article() {
  const { slug } = useParams();
  const article = CONTENT[slug];
  if (!article) {
    return <div className="max-w-3xl mx-auto p-16 text-center text-slate-500">Article not found. <Link to="/knowledge" className="text-[#0055FF]">Back to knowledge center</Link>.</div>;
  }
  return (
    <div data-testid="article-page" className="bg-white">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 pt-8 pb-2">
        <nav className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <Link to="/" className="hover:text-[#0055FF]">Home</Link>
          <ChevronsRight className="w-3 h-3" />
          <Link to="/knowledge" className="hover:text-[#0055FF]">Knowledge center</Link>
        </nav>
      </div>
      <article className="max-w-3xl mx-auto px-4 lg:px-8 py-8">
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">{article.title}</h1>
        <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
          {article.body.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </article>
    </div>
  );
}
